import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useJuice } from '../context/JuiceContext';
import {
  GameError,
  generateSpin,
  getAvailableActions,
  isVowel,
} from '../game';
import {
  actionPrompt,
  juiceEventForTransition,
  overlayKindForTransition,
  OverlayKind,
  playerDisplayName,
} from '../game/presentation';
import { humanizeFirebaseError, useGame } from '../hooks/useGame';
import { usePrefersReducedMotion } from '../hooks/usePrefersReducedMotion';
import { createGameDocument } from '../services/gameRepository';
import { shareGame } from '../utils/share';
import SlotMachine from './SlotMachine';
import ActionBanner from './play/ActionBanner';
import Celebration from './play/Celebration';
import GameOverScreen from './play/GameOverScreen';
import HowTo from './play/HowTo';
import LetterKeyboard from './play/LetterKeyboard';
import MuteButton from './play/MuteButton';
import PuzzleBoard from './play/PuzzleBoard';
import Scoreboard from './play/Scoreboard';
import SolveSheet from './play/SolveSheet';
import { ScreenError, ScreenLoading } from './play/ScreenState';

const GameBoard: React.FC = () => {
  const { gameId } = useParams<{ gameId: string }>();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const { play } = useJuice();
  const reducedMotion = usePrefersReducedMotion();
  const { game, loading, error, dispatch, setError } = useGame(gameId);
  const [isSpinning, setIsSpinning] = useState(false);
  const [isSolving, setIsSolving] = useState(false);
  const [howTo, setHowTo] = useState(false);
  const [overlay, setOverlay] = useState<OverlayKind>(null);
  const [overlayMessage, setOverlayMessage] = useState('');
  const [toast, setToast] = useState('');
  const [shareNote, setShareNote] = useState('');
  const [playingAgain, setPlayingAgain] = useState(false);
  const seenSpinAt = useRef<number | null>(null);
  const advancing = useRef(false);
  const prevGame = useRef(game);

  const playerId = currentUser?.uid || '';
  const actions = game ? getAvailableActions(game, playerId) : null;
  const current = game?.players[game.currentPlayerIndex];
  const prompt = useMemo(
    () => (game ? actionPrompt(game, playerId) : { headline: 'Loading', detail: '', tone: 'wait' as const }),
    [game, playerId]
  );

  useEffect(() => {
    if (game?.status === 'lobby') {
      navigate(`/game-lobby/${game.id}`, { replace: true });
    }
  }, [game, navigate]);

  useEffect(() => {
    if (!game?.lastSpinAt) return;
    if (seenSpinAt.current === game.lastSpinAt) return;
    seenSpinAt.current = game.lastSpinAt;
    setIsSpinning(true);
  }, [game?.lastSpinAt]);

  useEffect(() => {
    if (!game) {
      prevGame.current = game;
      return;
    }
    const prev = prevGame.current;
    const juice = juiceEventForTransition(prev, game);
    const nextOverlay = overlayKindForTransition(prev, game);
    if (juice === 'hit' || juice === 'miss' || juice === 'solve' || juice === 'win') {
      play(juice);
    }
    if (nextOverlay === 'solve' || nextOverlay === 'win') {
      setOverlay(nextOverlay);
      setOverlayMessage(game.message);
    }
    if (prev && game.message !== prev.message && prev.players[prev.currentPlayerIndex]?.id !== playerId) {
      setToast(game.message);
    }
    prevGame.current = game;
  }, [game, play, playerId]);

  useEffect(() => {
    if (!toast && !shareNote) return undefined;
    const timer = window.setTimeout(() => {
      setToast('');
      setShareNote('');
    }, 2400);
    return () => window.clearTimeout(timer);
  }, [toast, shareNote]);

  useEffect(() => {
    if (!game || !playerId || game.phase !== 'round_complete' || game.status !== 'playing') {
      advancing.current = false;
      return undefined;
    }
    if (!game.players.find((player) => player.id === playerId)?.isHost) {
      return undefined;
    }
    if (advancing.current) {
      return undefined;
    }
    advancing.current = true;
    const timer = window.setTimeout(async () => {
      try {
        await dispatch({ type: 'ADVANCE_ROUND', playerId });
      } catch (err) {
        if (!(err instanceof GameError) || !/not finished/i.test(err.message)) {
          setError(humanizeFirebaseError(err));
        }
      } finally {
        advancing.current = false;
      }
    }, 4500);
    return () => window.clearTimeout(timer);
  }, [game, playerId, dispatch, setError]);

  const run = async (work: () => Promise<unknown>) => {
    try {
      await work();
    } catch (err) {
      setIsSpinning(false);
      setError(humanizeFirebaseError(err));
    }
  };

  const handleSpin = () => {
    if (!actions?.canSpin || !playerId) return;
    play('click');
    setIsSpinning(true);
    void run(() => dispatch({ type: 'SPIN', playerId, spin: generateSpin() }));
  };

  const handleLanded = useCallback(() => {
    setIsSpinning(false);
    if (game?.lastSpin?.specialValue === 'BANKRUPT') {
      setOverlay('bankrupt');
      setOverlayMessage(game.message || 'Bankrupt!');
    }
  }, [game]);

  const handleLetterClick = (letter: string) => {
    if (!playerId || !actions) return;
    play('click');
    if (isVowel(letter)) {
      if (!actions.canBuyVowel) return;
      void run(() => dispatch({ type: 'BUY_VOWEL', playerId, letter }));
      return;
    }
    if (!actions.canGuessConsonant) return;
    void run(() => dispatch({ type: 'GUESS_CONSONANT', playerId, letter }));
  };

  const handleSolveAttempt = (guess: string) => {
    if (!playerId || !guess.trim()) return;
    setIsSolving(false);
    play('click');
    void run(() => dispatch({ type: 'SOLVE', playerId, guess }));
  };

  const handleEndGame = () => {
    if (!actions?.canEndGame || !playerId) return;
    if (window.confirm('End the game for everyone?')) {
      void run(() => dispatch({ type: 'END_GAME', playerId }));
    }
  };

  const handleShare = async () => {
    if (!game) return;
    play('click');
    const result = await shareGame(game.id);
    if (result === 'copied') {
      setShareNote('Invite copied');
    }
  };

  const handlePlayAgain = async () => {
    if (!currentUser) return;
    try {
      setPlayingAgain(true);
      const id = await createGameDocument(currentUser.uid, playerDisplayName(currentUser));
      navigate(`/game-lobby/${id}`);
    } catch (err) {
      setPlayingAgain(false);
      setError(humanizeFirebaseError(err));
    }
  };

  if (loading) {
    return <ScreenLoading play title="Loading the board" detail="The puzzle and scores update live." />;
  }

  if (!game) {
    return <ScreenError message={error || 'Game not found. Check the code and try again.'} onHome={() => navigate('/')} />;
  }

  const gameEnded = game.status === 'finished';
  const spinValue =
    game.phase === 'awaiting_consonant' && game.currentSpinValue != null
      ? game.currentSpinValue
      : game.lastSpin?.specialValue === 'BANKRUPT' || game.lastSpin?.specialValue === 'LOSE TURN'
        ? game.lastSpin.specialValue
        : game.currentSpinValue ?? 0;

  const showKeyboard = !gameEnded;
  const banner = toast || shareNote;

  return (
    <div className="ss-play is-board">
      <header className="ss-topbar">
        <div className="ss-topbar-meta">
          <div className="ss-kicker">
            Round {Math.max(1, game.currentRound)} / {game.totalRounds}
          </div>
          <button type="button" className="ss-code-chip" onClick={handleShare} aria-label={`Share game code ${game.id}`}>
            {game.id} · Share
          </button>
        </div>
        <MuteButton />
        <button type="button" className="ss-icon-btn" aria-label="How to play" onClick={() => setHowTo(true)}>
          ?
        </button>
        {actions?.isHost && !gameEnded && (
          <button type="button" className="ss-icon-btn" aria-label="End game" onClick={handleEndGame}>
            ✕
          </button>
        )}
      </header>

      {error && <div className="ss-error">{error}</div>}

      <ActionBanner prompt={prompt} />
      <Scoreboard
        players={game.players}
        currentPlayerId={current?.id}
        youId={playerId}
        gameEnded={gameEnded}
      />
      <PuzzleBoard
        phrase={game.currentPhrase}
        category={game.currentCategory}
        guessedLetters={game.guessedLetters}
        reducedMotion={reducedMotion}
      />

      <div className="ss-dock">
        {!gameEnded && (
          <SlotMachine
            isSpinning={isSpinning}
            spinValue={spinValue}
            reelValues={game.lastSpin?.reelValues ?? null}
            specialValue={game.lastSpin?.specialValue ?? null}
            onSpin={handleSpin}
            onLanded={handleLanded}
            isMyTurn={Boolean(actions?.canSpin)}
            gameEnded={gameEnded}
          />
        )}
        {showKeyboard && (
          <LetterKeyboard
            guessedLetters={game.guessedLetters}
            phrase={game.currentPhrase}
            canGuessConsonant={Boolean(actions?.canGuessConsonant)}
            canBuyVowel={Boolean(actions?.canBuyVowel)}
            onLetter={handleLetterClick}
            disabled={isSpinning}
          />
        )}
        {actions?.canSolve && !gameEnded && (
          <div className="ss-actions">
            <button type="button" className="ss-btn ss-btn-primary ss-wide" onClick={() => setIsSolving(true)}>
              Solve puzzle
            </button>
          </div>
        )}
      </div>

      {banner && <div className="ss-toast">{banner}</div>}

      <Celebration kind={overlay} message={overlayMessage} onDone={() => setOverlay(null)} />
      <SolveSheet open={isSolving} onClose={() => setIsSolving(false)} onSolve={handleSolveAttempt} />
      <HowTo auto open={howTo} onClose={() => setHowTo(false)} />
      {gameEnded && (
        <GameOverScreen
          game={game}
          onPlayAgain={handlePlayAgain}
          onHome={() => navigate('/')}
          playingAgain={playingAgain}
        />
      )}
    </div>
  );
};

export default GameBoard;
